import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ButtonSize, PrimaryCTAButton } from "../../shared/CTAButtonComponents";
import { Input } from "../../shared/InputField";
import { Error, HStack } from "../../shared/utils";
import { Icon } from "@iconify/react";
import { logoDarkImage } from "../../shared/images";
import { PrivacyPolicy } from "./privacyPolicy";
import PasswordStrengthIndicator from "./passwordIndicator";
import { useInitiateRegister, useRegisterUser } from "../../queries/auth";
import { UserType } from "../../types/auth";

const ErrorMessage = ({ text }: { text: string }) => (
  <Error className="text-left md:w-[400px] mx-auto" text={text} />
);

export default function RegistrationPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpBadge, setShowOtpBadge] = useState(false);
  const [transactionId, setTransactionId] = useState("");

  const navigate = useNavigate();
  const { mutate: initiateRegister } = useInitiateRegister();
  const { mutate: registerUser } = useRegisterUser();

  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({});

  // Auto-hide OTP badge after 5 seconds
  useEffect(() => {
    if (showOtpBadge) {
      const timer = setTimeout(() => {
        setShowOtpBadge(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showOtpBadge]);

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Please enter your full name";
        if (value.trim().length < 2)
          return "Name must be at least 2 characters";
        return "";
      case "email":
        if (!value) return "Please enter email address";
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value))
          return "Wrong email format";
        return "";
      case "otp":
        if (!value) return "Please enter OTP";
        if (value.length !== 6) return "OTP must be 6 digits";
        if (!/^[0-9]+$/.test(value)) return "OTP must contain only numbers";
        return "";
      case "password":
        if (!value) return "Please enter password";
        if (value.length < 8) return "Password must be at least 8 characters";
        return "";
      case "confirmPassword":
        if (!value) return "Please confirm your password";
        if (value !== formValues.password) return "Passwords do not match";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev: any) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev: any) => ({ ...prev, [name]: true }));
    setErrors((prev: any) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleContinue = () => {
    const nameError = validateField("name", formValues.name);
    const emailError = validateField("email", formValues.email);

    if (nameError || emailError) {
      setTouched((prev: any) => ({ ...prev, name: true, email: true }));
      setErrors((prev: any) => ({
        ...prev,
        name: nameError,
        email: emailError,
      }));
      return;
    }
    initiateRegister(
      { data: { email: formValues.email, fullName: formValues.name } },
      {
        onSuccess: (data) => {
          toast("OTP sent successfully", {
            type: "success",
            autoClose: 3000,
          });
          setTransactionId(data.data.transactionId);
          setStep(2);
          setShowOtpBadge(true);
          setResendTimer(30);

          const timer = setInterval(() => {
            setResendTimer((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        },
        onError: (error: any) => {
          toast(error?.response?.data?.message || "Failed to send OTP. Please try again.", {
            type: "error",
            autoClose: 3000,
          });
        },
      }
    );
  };

  const handleResendOTP = () => {
    toast("OTP resent successfully", { type: "success", autoClose: 3000 });
    setShowOtpBadge(true);
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleBackToEmail = () => {
    setStep(1);
    setFormValues({
      name: "",
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
    });
    setErrors({});
    setTouched({});
    setResendTimer(0);
    setTransactionId("");
    setShowOtpBadge(false);
  };

  const handleSubmit = () => {
    const otpError = validateField("otp", formValues.otp);
    const passwordError = validateField("password", formValues.password);
    const confirmError = validateField(
      "confirmPassword",
      formValues.confirmPassword
    );

    setTouched((prev: any) => ({
      ...prev,
      otp: true,
      password: true,
      confirmPassword: true,
    }));
    setErrors((prev: any) => ({
      ...prev,
      otp: otpError,
      password: passwordError,
      confirmPassword: confirmError,
    }));

    if (otpError || passwordError || confirmError) return;

    
    registerUser(
      {
        data: {
          email: formValues.email,
          userType: UserType.EQUION,
          password: formValues.password,
          confirmPassword: formValues.confirmPassword,
          otp: formValues.otp,
          transactionId: transactionId,
        },
      },
      {
        onSuccess: () => {
          proceedAfterRegistration();
        },
        onError: (error: any) => {
          toast(error?.response?.data?.message || "Registration failed. Please try again.", {
            type: "error",
            autoClose: 3000,
          });
        },
      }
    );
  };

  const proceedAfterRegistration = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast("Registration successful!", { type: "success", autoClose: 2000 });
      navigate("/login");
    }, 2000);
  };

  return (
    <div className="fixed top-0 right-0 left-0 z-40 bg-white shadow-box">
      <div
        className={`flex h-14 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-6`}
      >
        {logoDarkImage ? (
          <img className="h-9 w-auto" src={logoDarkImage} alt="hissa_logo" />
        ) : (
          <h2 className="text-4xl font-semibold text-secondary">{"hissa"}</h2>
        )}
      </div>

      <div className="w-screen px-4 px-md-0 h-screen bg-gradient-to-t from-gradientFrom to-gradientTo flex flex-col items-center justify-start overflow-y-auto">
        {/* Card 1: Name + Email */}
        <div
          className={`w-full max-w-[500px] p-4 sm:p-26 bg-white rounded-lg shadow-lg mt-6 h-[550px]
             ${step === 1 ? "opacity-100" : "opacity-0 hidden"}`}
        >
          <div className="flex flex-col justify-center items-center h-full">
            <div className="pt-[15px]">
              {logoDarkImage ? (
                <img
                  className="h-12 mx-auto w-28 mb-8"
                  src={logoDarkImage}
                  alt="hissa_logo"
                />
              ) : (
                <h2 className="text-4xl font-semibold text-center text-[#181C32] mb-8">
                  hissa
                </h2>
              )}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleContinue();
              }}
              className="w-full"
            >
              <p className="text-xs text-center text-gray-600 max-w-[450px] mx-auto my-6">
                {
                  "Create your account to explore your portfolio and access to marketplace"
                }
              </p>

              <div className="space-y-4 max-w-[400px] mx-auto">
                <div>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Full Name"
                    value={formValues.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.name && errors.name && (
                    <ErrorMessage text={errors.name} />
                  )}
                </div>

                <div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Email Address"
                    value={formValues.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.email && errors.email && (
                    <ErrorMessage text={errors.email} />
                  )}
                </div>

                <div className="flex justify-center pt-4">
                  <PrimaryCTAButton
                    className="w-32 text-center px-auto"
                    buttonSize={ButtonSize.MEDIUM}
                    onClick={handleContinue}
                  >
                    <p className="w-full text-sm leading-0">Continue</p>
                  </PrimaryCTAButton>
                </div>
              </div>
            </form>

            <div className="text-center mt-12">
              <div className="mb-6">
                <span className="text-sm text-gray-600">
                  Already have an account?{" "}
                </span>
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-[#EF5B2D] hover:text-[#d94f23] hover:underline"
                >
                  Sign In
                </button>
              </div>
              <p className="text-xs text-gray-400 leading-none">
                By clicking on Continue you are agreeing to the{" "}
                <span className="inline">
                  <PrivacyPolicy variant="light" inline />
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Card 2: OTP + Passwords */}
        <div
          className={`relative w-full max-w-[500px] p-4 sm:p-6 bg-white rounded-lg shadow-lg mt-6 h-[550px]
                 ${step === 2 ? "opacity-100" : "opacity-0 hidden"}`}
        >
          <div className="flex flex-col justify-center items-center h-full pt-4 pb-4 ">
            <div className="pt-[15px]">
              {logoDarkImage ? (
                <img
                  className="h-12 mx-auto w-28 mb-6"
                  src={logoDarkImage}
                  alt="hissa_logo"
                />
              ) : (
                <h2 className="text-4xl font-semibold text-center text-[#181C32] mb-6">
                  hissa
                </h2>
              )}
            </div>

            {/* OTP Sent Badge - Green, Auto-dismiss after 5 seconds */}
            {showOtpBadge && (
              <div className="bg-green-50 rounded-md p-1 w-full md:w-[400px] mx-auto mb-2 transition-opacity duration-300">
                <HStack className="flex text-center items-center">
                  <HStack className="gap-2">
                    <Icon
                      icon="mdi:check-circle"
                      className="text-green-600"
                      height={20}
                    />
                    <p className="text-sm text-green-700">
                      OTP sent to your email
                    </p>
                  </HStack>
                </HStack>
              </div>
            )}

            {/* Email Badge with Close Button */}
            <div className="bg-gray-100 rounded-md p-2 w-full md:w-[400px] mx-auto mb-4">
              <HStack className="justify-between">
                <div></div>
                <HStack>
                  <img
                    className="w-5 h-5 mr-2"
                    src="/media/user-circle.svg"
                    alt="user"
                  />
                  <p className="truncate text-sm">{formValues.email}</p>
                </HStack>
                <div className="flex items-center cursor-pointer">
                  <Icon
                    icon="zondicons:close-solid"
                    className="text-gray-400 hover:bg-gray-300 rounded-lg"
                    height={16}
                    onClick={handleBackToEmail}
                  />
                </div>
              </HStack>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="w-full"
            >
              <div className="space-y-3 max-w-[400px] mx-auto">
                <div>
                  <Input
                    id="otp"
                    name="otp"
                    type="number"
                    placeholder="Enter OTP"
                    value={formValues.otp}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      {touched.otp && errors.otp && (
                        <span className="text-[10px] text-red-500">
                          {errors.otp}
                        </span>
                      )}
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      {resendTimer > 0 ? (
                        <span className="text-[10px] text-gray-600 whitespace-nowrap">
                          Resend OTP in{" "}
                          <span className="text-primary font-semibold">
                            0:{resendTimer.toString().padStart(2, "0")}
                          </span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendOTP}
                          className="underline text-[10px] text-secondary font-semibold whitespace-nowrap"
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mx-auto w-full md:w-[400px] mt-2">
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      autoComplete="new-password"
                      value={formValues.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {!showPassword ? (
                        <Icon
                          icon="garden:eye-hide-fill-12"
                          className="text-gray-400 placeholderLoader rounded-lg"
                          height={18}
                        />
                      ) : (
                        <Icon
                          icon="subway:eye"
                          className="text-gray-400 placeholderLoader rounded-lg"
                          height={18}
                        />
                      )}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <ErrorMessage text={errors.password} />
                  )}
                </div>
                <div className="mx-auto w-full md:w-[400px] mt-2">
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      autoComplete="new-password"
                      value={formValues.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 mt-2 right-3 flex items-center"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <Icon
                        icon={
                          showConfirmPassword
                            ? "subway:eye"
                            : "garden:eye-hide-fill-12"
                        }
                        height={18}
                        className="text-gray-400"
                      />
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <ErrorMessage text={errors.confirmPassword} />
                  )}
                </div>
                <PasswordStrengthIndicator password={formValues.password} />
                <div className="flex justify-center pt-2">
                  <PrimaryCTAButton
                    className="w-32 text-center px-auto"
                    buttonSize={ButtonSize.MEDIUM}
                    loading={loading}
                    onClick={handleSubmit}
                  >
                    <p className="w-full text-sm leading-0">Sign Up</p>
                  </PrimaryCTAButton>
                </div>
              </div>
            </form>
            <div className="text-center mt-2">
              {" "}
              <div className="mb-2">
                <span className="text-xs text-gray-600">
                  Already have an account?{" "}
                </span>
                <button
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-[#EF5B2D] hover:text-[#d94f23] hover:underline"
                >
                  Sign In
                </button>
              </div>
              {/* <p className="text-xs text-gray-400 leading-none">
              By clicking on Sign up you are agreeing to the{" "} 
              <span className="inline"> 
                <PrivacyPolicy variant="light" inline />
              </span>
            </p> */}
            </div>
          </div>
        </div>
        {/* {step === 1 && (
        <div className="mt-2 mb-10">
          <PrivacyPolicy />
        </div>
      )} */}
      </div>
    </div>
  );
}
